import type { FC, ReactNode } from 'react';

import type { ItemDecision, ItemState } from '@eluve/api-contract';
import { Button, Icon, Stack, Tag, cn, tv } from '@eluve/components';
import type { TagProps } from '@eluve/components';

const rowStyles = tv({
  base: 'w-full rounded-lg border p-3.5 shadow-sm transition-all',
  variants: {
    decision: {
      approved: 'border-border-positive-subtle bg-bg-positive-subtle',
      edited: 'border-border-insights-subtle bg-bg-insights-subtle',
      rejected:
        'border-border-negative-subtle bg-bg-negative-subtle opacity-55',
      pending: 'border-border-secondary bg-bg-primary',
    },
  },
});

const DECISION_TAG: Record<
  'approved' | 'edited' | 'rejected',
  { label: string; color: TagProps['color'] }
> = {
  approved: { label: 'Confirmed', color: 'positive' },
  edited: { label: 'Edited', color: 'insights' },
  rejected: { label: 'Rejected', color: 'negative' },
};

export const ItemReviewRow: FC<{
  itemState: ItemState;
  decision: ItemDecision;
  title: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  onConfirm?: () => void;
  onReject?: () => void;
  onUndo?: () => void;
  onEdit?: () => void;
}> = ({
  itemState,
  decision,
  title,
  children,
  disabled,
  onConfirm,
  onReject,
  onUndo,
  onEdit,
}) => {
  const reviewable = itemState !== 'unchanged';
  const struck = reviewable && decision === 'rejected';

  return (
    <div className={rowStyles({ decision: reviewable ? decision : 'pending' })}>
      <Stack
        direction="horizontal"
        align="center"
        justify="between"
        gap={3}
        wrap
      >
        <div
          className={cn(
            'min-w-0 flex-1 text-sm font-medium',
            struck && 'line-through',
          )}
        >
          {title}
        </div>
        <Stack
          direction="horizontal"
          align="center"
          gap={1}
          fullWidth={false}
          className="shrink-0"
        >
          {/* A decided, reviewable row shows its verdict before row actions. */}
          {reviewable && decision !== 'pending' && (
            <Tag
              size="s"
              subtle={false}
              color={DECISION_TAG[decision].color}
              label={DECISION_TAG[decision].label}
              className="h-[30px] rounded-lg px-3"
            />
          )}
          {/* Confirm/Reject only while a reviewable row is still pending. */}
          {reviewable && decision === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled || !onConfirm}
              className="border-border-positive-subtle bg-bg-positive-subtle text-text-positive hover:bg-bg-positive-subtle"
              onClick={onConfirm}
            >
              <Icon name="Check" size="auto" />
              Confirm
            </Button>
          )}
          {/* Edit is always available — a decided or unchanged item can still be
              corrected (design: pencil present in every state). */}
          <Button
            size="sm"
            variant="outline"
            disabled={disabled || !onEdit}
            onClick={onEdit}
            aria-label="Edit"
          >
            <Icon name="Pencil" size="auto" />
          </Button>
          {reviewable && decision === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled || !onReject}
              className="border-border-negative-subtle bg-bg-negative-subtle text-text-negative hover:bg-bg-negative-subtle"
              onClick={onReject}
              aria-label="Reject"
            >
              <Icon name="X" size="auto" />
            </Button>
          )}
          {reviewable && decision !== 'pending' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled || !onUndo}
              onClick={onUndo}
            >
              <Icon name="Undo2" size="auto" />
              Undo
            </Button>
          )}
        </Stack>
      </Stack>
      {children}
    </div>
  );
};
